import type { CaseStudy } from '../types';

export const crisis2008: CaseStudy = {
  id: 'crisis-2008',
  order: 7,
  title: '2008 金融危机',
  english: 'The 2008 Financial Crisis',
  category: 'finance',
  era: '2007 — 2009',
  region: '美国 → 全球',
  accent: '#a7782f',
  hook: '一场危机不是某个坏人按下的按钮，而是一条把放贷、评级、杠杆和短期融资串起来的激励链，在房价下跌时同时断裂。',
  question: '为什么分散的、看似安全的金融产品，会在同一时间一起亏损？',
  summary:
    '低利率与房价上涨让高杠杆看起来可持续；证券化把贷款风险切分、重新包装，评级与短期融资进一步放大了系统。当房价转向，相关性上升、抵押品缩水、挤兑与甩卖互相强化。',
  thesis: '金融系统的脆弱，往往不在单个产品的平均风险，而在「压力来时大家同时需要现金」的相关性。',
  quote: '分散风险的前提是它们不会一起出事；如果所有资产都押在同一件事上，分散只是幻觉。',
  entryNodeIds: ['crisis-originate', 'crisis-securitize', 'crisis-ratings'],
  focusNodeId: 'crisis-originate',
  lensIds: ['mismatch', 'incentives', 'asymmetry', 'reflexivity'],
  opportunityIds: ['opp-liquidity-stress'],
  sourceIds: ['fcic-report', 'fed-great-recession'],
  facts: [
    {
      text: '2007–2009 年，美国房价下跌、次级抵押贷款违约上升，金融机构出现巨额损失，雷曼兄弟于 2008 年 9 月 15 日申请破产保护。',
      sourceIds: ['fcic-report', 'fed-great-recession'],
    },
    {
      text: '美国金融危机调查委员会（FCIC）认为危机本可避免，并将其归因于金融监管失败、公司治理失败、过度借贷与高风险投资等多重因素。',
      sourceIds: ['fcic-report'],
    },
    {
      text: '危机通过全球金融联系与贸易收缩扩散，演变为自大萧条以来最严重的全球衰退之一。',
      sourceIds: ['fed-great-recession'],
    },
  ],
  nodes: [
    {
      id: 'crisis',
      caseId: 'crisis-2008',
      kind: 'case',
      stage: 0,
      lane: 0,
      title: '2008 金融危机',
      summary: '房价、证券化、杠杆与短期融资同时反转。',
      detail:
        '危机不是单一事件，而是一组相互强化的机制：放贷方把贷款卖出去，投资者用评级和模型判断风险，金融机构用短期资金加杠杆持有长期资产。房价下跌让这些环节同时承压，最终以破产、救助和衰退收场。',
      boundary: '本案例以美国为中心，强调机制而非完整编年史；欧洲主权债务危机等后续发展另属一条线索。',
      evidence: 'fact',
      confidence: 'high',
      sourceIds: ['fcic-report'],
    },
    {
      id: 'crisis-originate',
      caseId: 'crisis-2008',
      kind: 'mechanism',
      stage: 1,
      lane: -1,
      title: '发起—分销：做贷款的人不持有贷款',
      summary: '放贷方把贷款卖给证券化机构，自己不再承担违约后果。',
      detail:
        '当贷款可以被迅速打包出售，放贷方的收入来自成交量而非还款质量。经纪人、评估师和借款人都可能在高房价预期下放松标准。风险没有消失，而是被转移给最终投资者；如果转移链条不透明，系统就失去了刹车。',
      boundary: '证券化本身可以分散风险、降低融资成本；问题在于激励、信息披露与风险留存是否合理。',
      evidence: 'mechanism',
      confidence: 'high',
      sourceIds: ['fcic-report'],
    },
    {
      id: 'crisis-securitize',
      caseId: 'crisis-2008',
      kind: 'mechanism',
      stage: 1,
      lane: 1,
      title: '证券化：把贷款切成“安全”的层级',
      summary: '不同层级的现金流被重新分配，复杂结构让风险看起来比实际更分散。',
      detail:
        '住房抵押贷款被打包成证券，按优先/次级结构分配现金流。优先级看似安全，因为它先拿钱；但当房价普遍下跌、违约同时上升，相关性急剧上升，原本的“分散”失效。复杂度还让投资者难以看清底层资产。',
      boundary: '分层结构在正常时期可以满足不同风险偏好；它不能消除系统性房价风险。',
      evidence: 'mechanism',
      confidence: 'high',
      sourceIds: ['fcic-report'],
    },
    {
      id: 'crisis-ratings',
      caseId: 'crisis-2008',
      kind: 'mechanism',
      stage: 2,
      lane: -1,
      title: '评级与模型：把复杂变成 AAA',
      summary: '评级机构由发行方付费，模型低估了全国房价同时下跌的可能性。',
      detail:
        '投资者依赖评级判断风险，评级机构则从发行方获得收入。模型基于历史数据，假设地区之间相对独立；当全国性房价下跌发生，历史相关性失效。评级不是恶意造假，也可能是激励与模型共同作用下的系统性误判。',
      boundary: '评级机构并非唯一责任方；投资者、监管者与发行方都在使用和放大这些信号。',
      evidence: 'mechanism',
      confidence: 'high',
      sourceIds: ['fcic-report'],
    },
    {
      id: 'crisis-leverage',
      caseId: 'crisis-2008',
      kind: 'mechanism',
      stage: 2,
      lane: 1,
      title: '杠杆与短期融资',
      summary: '用短期回购资金持有长期证券，抵押品价格一跌就触发追加保证金。',
      detail:
        '金融机构用较少的自有资本，借入大量短期资金持有长期资产。正常时，资产收益率高于借款成本，股本回报被放大；压力时，抵押品折价、对手方收紧融资，机构被迫卖资产，价格进一步下跌。',
      boundary: '杠杆能提高资本效率，但需要与资产流动性、资本缓冲和融资期限匹配。',
      evidence: 'mechanism',
      confidence: 'high',
      sourceIds: ['fcic-report'],
    },
    {
      id: 'crisis-condition',
      caseId: 'crisis-2008',
      kind: 'condition',
      stage: 2,
      lane: 0,
      title: '前提：低利率与房价上涨预期',
      summary: '长期低利率、全球储蓄流入和房价持续上涨，让高风险贷款看起来安全。',
      detail:
        '当房价上涨，借款人可以再融资或卖房还款，违约率被掩盖；投资者也愿意相信抵押品会保值。低利率推高资产估值，鼓励加杠杆。外部条件一旦反转，原本“合理”的假设会同时失效。',
      boundary: '低利率不是危机的充分条件；制度、监管与激励结构决定它是否被用来过度加杠杆。',
      evidence: 'inference',
      confidence: 'high',
      sourceIds: ['fcic-report', 'fed-great-recession'],
    },
    {
      id: 'crisis-outcome',
      caseId: 'crisis-2008',
      kind: 'outcome',
      stage: 3,
      lane: 0,
      title: '结果：信贷冻结与全球衰退',
      summary: '机构破产、救助与信贷收缩接连发生，实体经济被拖入衰退。',
      detail:
        '雷曼破产后，货币市场与银行间信任骤降，信贷收缩，企业投资与消费下降。危机从金融系统溢出到就业、贸易和公共财政。政策回应包括降息、救助与财政刺激，但代价与公平性至今仍有争论。',
      boundary: '不同国家的复苏速度和政策效果差异很大，不能把美国经历直接套用到所有市场。',
      evidence: 'fact',
      confidence: 'high',
      sourceIds: ['fed-great-recession', 'fcic-report'],
    },
    {
      id: 'crisis-risk',
      caseId: 'crisis-2008',
      kind: 'risk',
      stage: 4,
      lane: -1,
      title: '脆弱处：相关性在压力中上升',
      summary: '平时看起来不相关的资产，在恐慌中可能同时下跌、同时需要流动性。',
      detail:
        '风险模型常用历史相关性估算分散效果，但危机时相关性会上升：所有机构都想卖同类资产，所有抵押品同时折价，所有对手方同时收紧。真正的系统性风险不是平均损失，而是同时发生的流动性需求。',
      boundary: '相关性问题并非金融独有；供应链、平台生态和房地产也有类似的共同暴露。',
      evidence: 'mechanism',
      confidence: 'high',
      sourceIds: ['fcic-report'],
    },
    {
      id: 'crisis-opp',
      caseId: 'crisis-2008',
      kind: 'opportunity',
      stage: 4,
      lane: 1,
      title: '机会透镜：卖“压力下的现金流”',
      summary: '能帮助别人看清流动性、相关性与契约后果的服务，会比收益承诺更有价值。',
      detail:
        '对创业者与投资者，实用的方向包括：现金流压力测试、抵押品与交易对手风险工具、合规与披露、困境资产处置、保险与对冲。核心是回答：如果融资突然收紧，谁能在不压价的情况下活下来？',
      boundary: '危机工具容易在平静期被忽视；需求有明显的周期性，且专业责任与监管要求高。',
      evidence: 'inference',
      confidence: 'medium',
      sourceIds: ['fcic-report'],
    },
  ],
  edges: [
    {
      id: 'crisis-e1',
      caseId: 'crisis-2008',
      from: 'crisis',
      to: 'crisis-originate',
      label: '放贷激励改变',
      explanation: '贷款可以转卖后，放贷方更关注成交量而非长期还款质量。',
      tone: 'cause',
    },
    {
      id: 'crisis-e2',
      caseId: 'crisis-2008',
      from: 'crisis',
      to: 'crisis-securitize',
      label: '风险被重新包装',
      explanation: '贷款被打包、分层并出售，风险从银行资产负债表转移到投资者。',
      tone: 'cause',
    },
    {
      id: 'crisis-e3',
      caseId: 'crisis-2008',
      from: 'crisis-originate',
      to: 'crisis-securitize',
      label: '供给推动打包',
      explanation: '更多高风险贷款需要被证券化吸收，产品复杂度随之上升。',
      tone: 'cause',
    },
    {
      id: 'crisis-e4',
      caseId: 'crisis-2008',
      from: 'crisis-securitize',
      to: 'crisis-ratings',
      label: '复杂结构需要评级',
      explanation: '投资者难以逐笔审查底层贷款，转而依赖评级与模型。',
      tone: 'enable',
    },
    {
      id: 'crisis-e5',
      caseId: 'crisis-2008',
      from: 'crisis-securitize',
      to: 'crisis-leverage',
      label: '证券成为抵押品',
      explanation: '看似安全的证券被用于抵押融资，杠杆进一步放大。',
      tone: 'cause',
    },
    {
      id: 'crisis-e6',
      caseId: 'crisis-2008',
      from: 'crisis-ratings',
      to: 'crisis-outcome',
      label: '错误信号',
      explanation: '高评级让机构与投资者持有超出其风险承受能力的资产。',
      tone: 'cause',
    },
    {
      id: 'crisis-e7',
      caseId: 'crisis-2008',
      from: 'crisis-leverage',
      to: 'crisis-outcome',
      label: '融资链断裂',
      explanation: '抵押品折价与追加保证金迫使机构抛售，信贷收缩扩散到实体。',
      tone: 'cause',
    },
    {
      id: 'crisis-e8',
      caseId: 'crisis-2008',
      from: 'crisis-condition',
      to: 'crisis-outcome',
      label: '假设反转',
      explanation: '房价上涨与低利率的假设同时失效，违约与估值下跌互相强化。',
      tone: 'enable',
    },
    {
      id: 'crisis-e9',
      caseId: 'crisis-2008',
      from: 'crisis-outcome',
      to: 'crisis-risk',
      label: '相关性暴露',
      explanation: '危机证明“分散”在共同暴露面前会失效，流动性需求同时爆发。',
      tone: 'risk',
    },
    {
      id: 'crisis-e10',
      caseId: 'crisis-2008',
      from: 'crisis-risk',
      to: 'crisis-opp',
      label: '压力测试需求',
      explanation: '能看清压力下现金流与相关性的工具和服务，具有持续价值。',
      tone: 'enable',
    },
  ],
};
